import { useState, useEffect } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

const CATEGORIES = [
  { key: "material", label: "Material" },
  { key: "labor", label: "Labor" },
  { key: "subcontract", label: "Subcontract" },
  { key: "freight", label: "Freight" },
  { key: "equipment", label: "Equipment" },
  { key: "travel", label: "Travel" },
  { key: "disposal", label: "Disposal" },
];

const fmt = (v) => Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
export const pct = (revenue, cost) => revenue > 0 ? Math.round(((revenue - cost) / revenue) * 100) : 0;

function aggregateCOBudgets(changeOrders) {
  const agg = {};
  CATEGORIES.forEach(c => { agg[c.key] = { cost: 0, revenue: 0 }; });
  (changeOrders || []).filter(co => co.status === "approved").forEach(co => {
    const coBudget = co.budget;
    if (coBudget) {
      CATEGORIES.forEach(c => {
        const cat = coBudget[c.key];
        if (cat) {
          agg[c.key].cost += cat.cost || 0;
          agg[c.key].revenue += cat.revenue || 0;
        }
      });
    }
  });
  return agg;
}

// Roll-up used by the Budget card: base (WO) vs. current (WO + approved COs).
export function summarizeBudget(budget, changeOrders) {
  if (!budget) return null;
  const co = aggregateCOBudgets(changeOrders);
  const t = CATEGORIES.reduce((acc, c) => {
    const cat = budget[c.key] || { cost: 0, revenue: 0 };
    acc.baseRevenue += cat.revenue || 0; acc.baseCost += cat.cost || 0;
    acc.coRevenue += co[c.key].revenue; acc.coCost += co[c.key].cost;
    return acc;
  }, { baseRevenue: 0, baseCost: 0, coRevenue: 0, coCost: 0 });
  const currentRevenue = t.baseRevenue + t.coRevenue;
  const currentCost = t.baseCost + t.coCost;
  return {
    ...t, currentRevenue, currentCost,
    baseGMPct: pct(t.baseRevenue, t.baseCost), currentGMPct: pct(currentRevenue, currentCost),
    hasData: t.baseRevenue > 0 || t.baseCost > 0 || t.coRevenue > 0 || t.coCost > 0,
  };
}

// Compact 5-column current-state table for the Budget card (full-page WO).
// `actuals` (optional) = { category: posted cost } from Foundation; adds Actual + Actual GM % columns.
// `committed` = outstanding open-PO dollars by category (Approved/Ordered/Partial) — the money already spoken for.
// `tax` = the WO's LIVE sales tax (material only). The Tax row is always shown, even at $0.00, and Total includes it.
export function BudgetBreakdown({ budget, changeOrders, actuals, committed, extraLabels = {}, tax = null }) {
  if (!budget) return null;
  const co = aggregateCOBudgets(changeOrders);
  const extra = Object.keys(actuals || {}).filter((k) => !CATEGORIES.some((c) => c.key === k) && actuals[k] && !(tax != null && k === "use_tax")).map((k) => ({ key: k, label: extraLabels[k] || k }));
  const cats = [...CATEGORIES, ...extra];
  const rows = cats.map((c) => {
    const cat = budget[c.key] || { cost: 0, revenue: 0 };
    const coCat = co[c.key] || { revenue: 0, cost: 0 };
    return { key: c.key, label: c.label, revenue: (cat.revenue || 0) + coCat.revenue, cost: (cat.cost || 0) + coCat.cost, actual: actuals?.[c.key] || 0, committed: committed?.[c.key] || 0 };
  }).filter((r) => r.revenue > 0 || r.cost > 0 || r.actual > 0 || r.committed > 0);
  if (!rows.length && tax == null) return <div className="text-xs text-slate-400" data-testid="budget-breakdown-empty">No budget detail yet.</div>;
  const tot = rows.reduce((a, r) => ({ revenue: a.revenue + r.revenue, cost: a.cost + r.cost, actual: a.actual + r.actual, committed: a.committed + r.committed }), { revenue: 0, cost: 0, actual: 0, committed: 0 });
  const taxAmt = Math.round((Number(tax) || 0) * 100) / 100;
  const taxActual = actuals?.use_tax || 0;
  const withTax = tax == null ? tot : { ...tot, revenue: tot.revenue + taxAmt, cost: tot.cost + taxAmt };
  const Row = ({ r, strong, testId, title }) => {
    const gm = r.revenue - r.cost;
    const agm = r.revenue - r.actual;
    return (
      <tr className={`border-t border-slate-100 ${strong ? "bg-slate-50 font-semibold" : ""}`} data-testid={testId} title={title}>
        <td className="py-1.5 px-2 text-slate-700">{r.label}</td>
        <td className="py-1.5 px-2 text-right num">{fmt(r.revenue)}</td>
        <td className="py-1.5 px-2 text-right num text-slate-500">{fmt(r.cost)}</td>
        <td className={`py-1.5 px-2 text-right num ${r.noGm ? "text-slate-400" : gm >= 0 ? "text-emerald-700" : "text-red-600"}`}>{r.noGm ? "—" : fmt(gm)}</td>
        <td className={`py-1.5 px-2 text-right num ${r.noGm ? "text-slate-400" : gm >= 0 ? "text-emerald-700" : "text-red-600"}`}>{r.noGm ? "—" : `${pct(r.revenue, r.cost)}%`}</td>
        {committed && <td className={`py-1.5 px-2 text-right num ${r.committed > 0 ? "text-amber-700" : "text-slate-400"}`} data-testid={testId ? `${testId}-committed` : undefined}>{r.committed > 0 ? fmt(r.committed) : "—"}</td>}
        {actuals && (
          <>
            <td className="py-1.5 px-2 text-right num text-slate-700">{fmt(r.actual)}</td>
            <td className={`py-1.5 px-2 text-right num ${r.noGm || r.revenue <= 0 ? "text-slate-400" : agm >= 0 ? "text-emerald-700" : "text-red-600"}`}>{!r.noGm && r.revenue > 0 ? `${pct(r.revenue, r.actual)}%` : "—"}</td>
          </>
        )}
      </tr>
    );
  };
  return (
    <div className="border rounded-lg overflow-x-auto" data-testid="budget-viewer-table">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-slate-50 text-slate-500">
            <th className="text-left py-1.5 px-2 font-semibold">Category</th>
            <th className="text-right py-1.5 px-2 font-semibold">Revenue</th>
            <th className="text-right py-1.5 px-2 font-semibold">Cost</th>
            <th className="text-right py-1.5 px-2 font-semibold">GM $</th>
            <th className="text-right py-1.5 px-2 font-semibold">GM %</th>
            {committed && <th className="text-right py-1.5 px-2 font-semibold" title="Outstanding on open POs (Approved / Ordered / Partial) — drops as material is received">Committed</th>}
            {actuals && (
              <>
                <th className="text-right py-1.5 px-2 font-semibold" title="Posted job cost from Foundation">Actual</th>
                <th className="text-right py-1.5 px-2 font-semibold" title="(Revenue − Foundation actual cost) ÷ Revenue">Actual GM %</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => <Row key={r.key} r={r} testId={`budget-row-${r.key}`} />)}
          {tax != null ? (
            <>
              <Row r={{ label: "Subtotal", ...tot }} strong testId="budget-row-subtotal" />
              <Row r={{ label: "Tax", revenue: taxAmt, cost: taxAmt, actual: taxActual, committed: 0, noGm: true }} testId="budget-row-tax"
                title="Live sales tax on material only — never freight, travel, disposal, equipment or labor" />
              <Row r={{ label: "Total", ...withTax }} strong testId="budget-row-total" />
            </>
          ) : (
            <Row r={{ label: "Total", ...tot }} strong testId="budget-row-total" />
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function BudgetViewer({ budget, changeOrders }) {
  const [open, setOpen] = useState(false);
  // Item E — listen for global expand/collapse-all toggle. Note: open is
  // inverted from `collapsed` (true = expanded), so we flip the value.
  useEffect(() => {
    const onAll = (e) => setOpen(!e.detail?.collapsed);
    window.addEventListener("wo:section-toggle-all", onAll);
    return () => window.removeEventListener("wo:section-toggle-all", onAll);
  }, []);

  if (!budget) return null;

  const hasOrigData = CATEGORIES.some(c => {
    const cat = budget[c.key];
    return cat && (cat.cost > 0 || cat.revenue > 0);
  });

  const hasCOs = (changeOrders || []).filter(co => co.status === "approved").some(co => co.budget && CATEGORIES.some(c => {
    const cat = co.budget[c.key];
    return cat && (cat.cost > 0 || cat.revenue > 0);
  }));

  if (!hasOrigData && !hasCOs) return null;

  const coBudget = aggregateCOBudgets(changeOrders);

  const origTotals = CATEGORIES.reduce(
    (acc, c) => {
      const cat = budget[c.key] || { cost: 0, revenue: 0 };
      acc.cost += cat.cost;
      acc.revenue += cat.revenue;
      return acc;
    },
    { cost: 0, revenue: 0 }
  );

  const coTotals = CATEGORIES.reduce(
    (acc, c) => {
      acc.cost += coBudget[c.key].cost;
      acc.revenue += coBudget[c.key].revenue;
      return acc;
    },
    { cost: 0, revenue: 0 }
  );

  const currentRevenue = origTotals.revenue + coTotals.revenue;
  const currentCost = origTotals.cost + coTotals.cost;
  const currentGM = currentRevenue - currentCost;

  const tax = budget.tax || { cost: 0, revenue: 0 };
  const showCOColumns = hasCOs;

  return (
    <div className="border-t pt-4 space-y-2" data-testid="budget-viewer-section">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 w-full text-left group"
        data-testid="budget-viewer-toggle"
      >
        <span className="font-semibold text-sm text-slate-700 flex-1">Budget</span>
        {!open && (
          <span className="text-xs text-slate-500 num">
            {showCOColumns
              ? `Original: ${fmt(origTotals.revenue)} | Current: ${fmt(currentRevenue)} | GM: ${fmt(currentGM)} (${pct(currentRevenue, currentCost)}%)`
              : `GM: ${fmt(origTotals.revenue - origTotals.cost)} (${pct(origTotals.revenue, origTotals.cost)}%)`
            }
          </span>
        )}
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>

      {open && (
        <div className="mt-3 border rounded-lg overflow-hidden" data-testid="budget-viewer-table">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-500">
                <th className="text-left py-2 px-3 font-semibold">Category</th>
                <th className="text-right py-2 px-3 font-semibold">Revenue</th>
                <th className="text-right py-2 px-3 font-semibold">Cost</th>
                <th className="text-right py-2 px-3 font-semibold">GM $</th>
                <th className="text-right py-2 px-3 font-semibold">GM %</th>
                {showCOColumns && (
                  <>
                    <th className="text-right py-2 px-3 font-semibold border-l">CO Revenue</th>
                    <th className="text-right py-2 px-3 font-semibold">CO Cost</th>
                    <th className="text-right py-2 px-3 font-semibold">CO GM $</th>
                    <th className="text-right py-2 px-3 font-semibold">CO GM %</th>
                    <th className="text-right py-2 px-3 font-semibold border-l">Current</th>
                    <th className="text-right py-2 px-3 font-semibold">Current GM %</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {CATEGORIES.map((c) => {
                const cat = budget[c.key] || { cost: 0, revenue: 0 };
                const co = coBudget[c.key];
                const origHasData = cat.cost > 0 || cat.revenue > 0;
                const coHasData = co.revenue > 0 || co.cost > 0;
                if (!origHasData && !coHasData) return null;

                const gm = cat.revenue - cat.cost;
                const gmPct = pct(cat.revenue, cat.cost);
                const coGM = co.revenue - co.cost;
                const curRev = cat.revenue + co.revenue;
                const curCost = cat.cost + co.cost;
                const curGM = curRev - curCost;

                return (
                  <tr key={c.key} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="py-1.5 px-3 font-medium text-slate-700">{c.label}</td>
                    <td className="py-1.5 px-3 text-right num">{fmt(cat.revenue)}</td>
                    <td className="py-1.5 px-3 text-right num text-slate-500">{fmt(cat.cost)}</td>
                    <td className={`py-1.5 px-3 text-right num font-medium ${gm >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmt(gm)}</td>
                    <td className={`py-1.5 px-3 text-right num font-medium ${gmPct >= 0 ? "text-emerald-700" : "text-red-600"}`}>{gmPct}%</td>
                    {showCOColumns && (
                      <>
                        <td className="py-1.5 px-3 text-right num font-medium border-l" data-testid={`co-rev-${c.key}`}>
                          {co.revenue > 0 ? fmt(co.revenue) : "—"}
                        </td>
                        <td className="py-1.5 px-3 text-right num text-slate-500" data-testid={`co-cost-${c.key}`}>
                          {co.cost > 0 ? fmt(co.cost) : "—"}
                        </td>
                        <td className={`py-1.5 px-3 text-right num font-medium ${coGM >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid={`co-gm-${c.key}`}>
                          {co.revenue > 0 ? fmt(coGM) : "—"}
                        </td>
                        <td className={`py-1.5 px-3 text-right num font-medium ${pct(co.revenue, co.cost) >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid={`co-gmp-${c.key}`}>
                          {co.revenue > 0 ? `${pct(co.revenue, co.cost)}%` : "—"}
                        </td>
                        <td className="py-1.5 px-3 text-right num font-medium border-l">
                          {fmt(curRev)}
                        </td>
                        <td className={`py-1.5 px-3 text-right num font-medium border-l ${pct(curRev, curCost) >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                          {pct(curRev, curCost)}%
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}

              {/* Subtotal */}
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                <td className="py-1.5 px-3 text-slate-700">Subtotal</td>
                <td className="py-1.5 px-3 text-right num">{fmt(origTotals.revenue)}</td>
                <td className="py-1.5 px-3 text-right num text-slate-500">{fmt(origTotals.cost)}</td>
                <td className={`py-1.5 px-3 text-right num ${origTotals.revenue - origTotals.cost >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmt(origTotals.revenue - origTotals.cost)}</td>
                <td className={`py-1.5 px-3 text-right num ${pct(origTotals.revenue, origTotals.cost) >= 0 ? "text-emerald-700" : "text-red-600"}`}>{pct(origTotals.revenue, origTotals.cost)}%</td>
                {showCOColumns && (
                  <>
                    <td className="py-1.5 px-3 text-right num font-semibold border-l">
                      {coTotals.revenue > 0 ? fmt(coTotals.revenue) : "—"}
                    </td>
                    <td className="py-1.5 px-3 text-right num font-semibold text-slate-500">
                      {coTotals.cost > 0 ? fmt(coTotals.cost) : "—"}
                    </td>
                    <td className={`py-1.5 px-3 text-right num font-semibold ${coTotals.revenue - coTotals.cost >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {coTotals.revenue > 0 ? fmt(coTotals.revenue - coTotals.cost) : "—"}
                    </td>
                    <td className={`py-1.5 px-3 text-right num font-semibold ${pct(coTotals.revenue, coTotals.cost) >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {coTotals.revenue > 0 ? `${pct(coTotals.revenue, coTotals.cost)}%` : "—"}
                    </td>
                    <td className="py-1.5 px-3 text-right num font-semibold border-l">
                      {fmt(currentRevenue)}
                    </td>
                    <td className={`py-1.5 px-3 text-right num font-semibold border-l ${pct(currentRevenue, currentCost) >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {pct(currentRevenue, currentCost)}%
                    </td>
                  </>
                )}
              </tr>

              {/* Tax */}
              {tax.revenue > 0 && (
                <tr className="border-t border-slate-200">
                  <td className="py-1.5 px-3 text-slate-500">Tax</td>
                  <td className="py-1.5 px-3 text-right num text-slate-500">{fmt(tax.revenue)}</td>
                  <td className="py-1.5 px-3 text-right num text-slate-500">{fmt(tax.cost)}</td>
                  <td className="py-1.5 px-3 text-right num text-slate-400">—</td>
                  <td className="py-1.5 px-3 text-right num text-slate-400">—</td>
                  {showCOColumns && (
                    <>
                      <td className="py-1.5 px-3 text-right border-l text-slate-400">—</td>
                      <td className="py-1.5 px-3 text-right text-slate-400">—</td>
                      <td className="py-1.5 px-3 text-right text-slate-400">—</td>
                      <td className="py-1.5 px-3 text-right text-slate-400">—</td>
                      <td className="py-1.5 px-3 text-right border-l text-slate-500">{fmt(tax.revenue)}</td>
                      <td className="py-1.5 px-3 text-right border-l text-slate-400">—</td>
                    </>
                  )}
                </tr>
              )}

              {/* Grand total */}
              <tr className="border-t-2 border-slate-400 bg-slate-100 font-bold text-sm">
                <td className="py-2 px-3 text-slate-800">Total</td>
                <td className="py-2 px-3 text-right num">{fmt(origTotals.revenue + tax.revenue)}</td>
                <td className="py-2 px-3 text-right num text-slate-600">{fmt(origTotals.cost + tax.cost)}</td>
                <td className={`py-2 px-3 text-right num ${origTotals.revenue - origTotals.cost >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmt(origTotals.revenue - origTotals.cost)}</td>
                <td className={`py-2 px-3 text-right num ${pct(origTotals.revenue, origTotals.cost) >= 0 ? "text-emerald-700" : "text-red-600"}`}>{pct(origTotals.revenue, origTotals.cost)}%</td>
                {showCOColumns && (
                  <>
                    <td className="py-2 px-3 text-right num border-l">
                      {coTotals.revenue > 0 ? fmt(coTotals.revenue) : "—"}
                    </td>
                    <td className="py-2 px-3 text-right num text-slate-600">
                      {coTotals.cost > 0 ? fmt(coTotals.cost) : "—"}
                    </td>
                    <td className={`py-2 px-3 text-right num ${coTotals.revenue - coTotals.cost >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {coTotals.revenue > 0 ? fmt(coTotals.revenue - coTotals.cost) : "—"}
                    </td>
                    <td className={`py-2 px-3 text-right num ${pct(coTotals.revenue, coTotals.cost) >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {coTotals.revenue > 0 ? `${pct(coTotals.revenue, coTotals.cost)}%` : "—"}
                    </td>
                    <td className="py-2 px-3 text-right num border-l font-bold">
                      {fmt(currentRevenue + tax.revenue)}
                    </td>
                    <td className={`py-2 px-3 text-right num border-l ${pct(currentRevenue, currentCost) >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {pct(currentRevenue, currentCost)}%
                    </td>
                  </>
                )}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
